import { ReactNode } from 'react';

import classNames from 'classnames';

import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';

import { getPublishedStatus, selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';

import useChildHeight from '../hooks/use-child-height';

import styles from './settings-edit-panel.module.css';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

interface ISettingsEditPanelProps {
  children: ReactNode;
  isEditing: boolean;
  onCancel?: () => void;
  onSave: () => void;
  onPreview?: () => void;
  warningMessage?: string;
  saveDisabled?: boolean;
  marginOnEdit?: number;
}

const SettingsEditPanel = ({
  children,
  isEditing,
  onCancel,
  onSave,
  onPreview,
  warningMessage,
  saveDisabled,
  marginOnEdit = 20,
}: ISettingsEditPanelProps) => {
  const isSaving = useAppSelector(selectIsSavingWorkingProject);
  const publishedStatus = useAppSelector(getPublishedStatus);

  const editContainerClasses = classNames(
    'flex-column',
    'gap-16',
    'jc-flex-end',
    'position-absolute',
    styles['settings-edit-container']
  );

  const [childrenWrapperRef, childrenHeight] = useChildHeight(children);

  return (
    <div className={styles['settings-widget']}>
      <Container
        className={styles['settings-detail-container']}
        margin={isEditing ? marginOnEdit + 'px' : '0'}
        height={isEditing ? `${childrenHeight + 40 + marginOnEdit}px` : `${childrenHeight}px`}
      >
        <div ref={childrenWrapperRef}>{children}</div>
      </Container>
      <Container
        className={editContainerClasses}
        background="var(--gray-900)"
        padding="20px"
        width="100%"
        height={isEditing ? `${childrenHeight + 75 + marginOnEdit}px` : '0px'}
        opacity={isEditing ? '1' : '0'}
        borderRadius="12px"
      >
        <div
          inert={!isEditing}
          className="flex ai-center jc-space-between"
          style={{
            ...(isEditing && { zIndex: '10' }),
          }}
        >
          <OptionalComponent display={!!onPreview}>
            <Button
              backgroundColor="var(--gray-700)"
              hoverBackgroundColor="var(--gray-600)"
              height="32px"
              className="L2 padding-v-7 padding-h-24"
              onClick={onPreview}
              disabled={!publishedStatus?.url}
              disabledOpacity={0.6}
            >
              Preview
            </Button>
          </OptionalComponent>
          <Text className="P2" color="var(--global-error-400)">
            {warningMessage}
          </Text>
          <div className="flex ai-center gap-8 flex-1 jc-flex-end">
            <OptionalComponent display={!!onCancel}>
              <Button
                backgroundColor="var(--gray-700)"
                hoverBackgroundColor="var(--gray-600)"
                height="32px"
                className="L2 padding-v-7 padding-h-24"
                onClick={onCancel}
                disabled={isSaving}
                disabledOpacity={0.6}
              >
                <Text className="L2">Cancel</Text>
              </Button>
            </OptionalComponent>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              padding="8px 24px"
              width="auto"
              onClick={onSave}
              disabled={isSaving || saveDisabled}
              disabledOpacity={0.6}
              hoverOpacity={0.8}
            >
              <Text color={getThemeColorContrastCSSVariable()} className="L2">
                Save
              </Text>
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default SettingsEditPanel;
